/**
 * Konfiguration zur Laufzeit.
 *
 * Warum nicht einfach import.meta.env: Coolify reicht eigene Variablen nicht
 * als Docker-Build-Argument durch, sie stehen nur im laufenden Container. Der
 * Container schreibt sie beim Start nach /config.json, die App liest sie von
 * dort. Nebeneffekt: ein Wechsel der Supabase-Instanz braucht keinen neuen
 * Build, nur einen Neustart.
 *
 * Der zuletzt gelesene Stand liegt im localStorage, damit die App auch offline
 * sofort weiß, wohin sie synchronisieren soll.
 */
export interface RuntimeConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  appUrl: string;
  /** Öffentlicher VAPID-Schlüssel. Leer heißt: keine Benachrichtigungen. */
  pushPublicKey: string;
}

const STORAGE_KEY = "ainkauf.config";

function fromBuild(): RuntimeConfig {
  return {
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL ?? "",
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? "",
    appUrl: import.meta.env.VITE_APP_URL ?? window.location.origin,
    pushPublicKey: import.meta.env.VITE_PUSH_PUBLIC_KEY ?? "",
  };
}

function fromCache(): RuntimeConfig | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RuntimeConfig>;
    if (!parsed.supabaseUrl || !parsed.supabaseAnonKey) return null;
    return {
      supabaseUrl: parsed.supabaseUrl,
      supabaseAnonKey: parsed.supabaseAnonKey,
      appUrl: parsed.appUrl ?? window.location.origin,
      pushPublicKey: parsed.pushPublicKey ?? "",
    };
  } catch {
    return null;
  }
}

let active: RuntimeConfig = fromCache() ?? fromBuild();

export function config(): RuntimeConfig {
  return active;
}

export function isConfigured(): boolean {
  return active.supabaseUrl.length > 0 && active.supabaseAnonKey.length > 0;
}

/** Holt /config.json. Schlägt das fehl (offline), gilt der Zwischenspeicher. */
export async function refreshConfig(): Promise<void> {
  try {
    const response = await fetch("/config.json", { cache: "no-store" });
    if (!response.ok) return;

    const data = (await response.json()) as Partial<RuntimeConfig>;
    if (!data.supabaseUrl || !data.supabaseAnonKey) return;

    active = {
      supabaseUrl: data.supabaseUrl,
      supabaseAnonKey: data.supabaseAnonKey,
      appUrl: data.appUrl ?? window.location.origin,
      pushPublicKey: data.pushPublicKey ?? "",
    };
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(active));
  } catch {
    // Kein Netz oder keine Datei: der zuletzt bekannte Stand bleibt gültig.
  }
}

/** Beim Start: ohne Zwischenspeicher wird gewartet, sonst im Hintergrund erneuert. */
export async function bootstrapConfig(): Promise<void> {
  if (fromCache() !== null) {
    void refreshConfig();
    return;
  }
  await refreshConfig();
}
